/**
 * Lời gọi API phân hệ "Bảng điện tử công cộng" (bảng tin tách khỏi cụm loa, hợp đồng A0
 * `docs/bang-tin/HOP-DONG-A0-BANG-TIN.md` mục 4) — tiền tố `/api/v1/digital-signage`.
 *
 * Dùng lại lớp gọi chung `goi-chung.ts` (phong bì, token, `LoiApi`) để `dungTaiDuLieu` nhận diện
 * lỗi như mọi màn hình khác.
 *
 * Gộp kho bản tin (hợp đồng `docs/bang-tin/HOP-DONG-A0-GOP-BAN-TIN.md`, 29/10/2026): bảng tin KHÔNG
 * còn kho bản tin riêng (`digital-signage/bulletins*` đã bỏ) — bản tin lấy từ Kho nội dung
 * (`GET /api/v1/bulletins`, `ban-tin-api.ts`) và gửi `id` (uuid `ban_tin.id`) cho API bảng tin.
 *
 * Khuôn dữ liệu theo hợp đồng endpoint cuối cùng A0 chuyển từ A3 (29/10/2026): thời điểm là epoch
 * giây, trừ `createdAt`/`updatedAt` là chuỗi ISO; danh sách `{items, total, page, pageSize}`.
 */
import { chuoiTruyVan, goi, taiTepTuApi } from './goi-chung';
import { giayTuThoiLuong, type DongBanTin } from './ban-tin-api';
import type { TrangDuLieu } from './thiet-bi-api';

export type { TrangDuLieu };

const GOC = '/api/v1/digital-signage';

/* --------------------------------- Kiểu chung -------------------------------- */

/** PhanVungManHinh của lịch: 0 toàn màn hình, 1 ba vùng. */
export type PhanVungManHinhBT = '0' | '1';
export type TrangThaiLichBT = 'hieu_luc' | 'da_huy' | 'het_hieu_luc';
export type LoaiLichBT = 'theo_ngay' | 'hang_ngay';

/** Lỗi gửi xuống từng bảng tin (trả từ service bảng tin qua API). */
export interface LoiGuiBT {
  bangDienTuId?: string;
  maLoi: string;
  thongBao: string;
}

/** Kết quả một lần gửi xuống thiết bị (phát ngay, khẩn cấp, điều khiển, phát lịch). */
export interface KetQuaGuiBT {
  phienId?: string;
  soLanPublish?: number;
  topic?: string[];
  soNhaCungCap?: number;
  loi?: LoiGuiBT[];
}

/* ---------------------------------- Bản tin --------------------------------- */

/**
 * Bản tin chọn vào lịch / phát ngay của bảng tin — rút từ dòng Kho nội dung (`DongBanTin`).
 * `vungPhat` của bản tin âm thanh để trống thì coi là `'0'`; thời lượng đổi từ `HH:mm:ss`.
 */
export interface BanTinChonBT {
  id: string;
  banTinId: string;
  tieuDe: string;
  loaiBanTin: string;
  vungPhat: string;
  thoiLuongGiay: number | null;
  trangThai: string;
}

export function banTinChonTuKho(d: DongBanTin): BanTinChonBT {
  return {
    id: d.id,
    banTinId: d.banTinId,
    tieuDe: d.tieuDe,
    loaiBanTin: d.loaiBanTin,
    vungPhat: d.vungPhat || '0',
    thoiLuongGiay: giayTuThoiLuong(d.thoiLuong),
    trangThai: d.trangThai,
  };
}

export interface LinkTepBT {
  url: string;
  hetHanGiay?: number;
  tepTen?: string;
  tepMime?: string;
  /** Có ở `play-logs/:id/playback`. */
  loaiBanTin?: string;
}

/* ---------------------------------- Lịch phát -------------------------------- */

export interface KhungGioBT {
  /** theo_ngay: epoch giây tuyệt đối; hang_ngay: giây trong ngày (0–86399, giờ VN). */
  batDau: number;
  ketThuc: number;
}

export interface PhamViLichBT {
  dichId: string;
  tenDich?: string;
  /** Mảng BangDienTuID; rỗng/không có = mọi bảng tin trên địa bàn. */
  bangDienTu?: string[];
}

/** Bản tin trong chi tiết lịch. */
export interface BanTinTrongLichBT {
  id: string;
  banTinId?: string;
  tieuDe?: string;
  loaiBanTin?: string;
  vungPhat?: string;
  thoiLuongGiay?: number | null;
  trangThai?: string;
  thuTu?: number;
}

/** Dòng danh sách lịch. */
export interface LichPhatBT {
  id: string;
  /** Mã lịch (sinh `LBT…`). */
  lichPhatId: string;
  tenLichPhat: string;
  loaiLich: LoaiLichBT;
  phanVungManHinh: PhanVungManHinhBT;
  ngayBatDauPhat?: number | null;
  ngayKetThucPhat?: number | null;
  trangThai: TrangThaiLichBT;
  soBanTin?: number;
  soKhungGio?: number;
  /** Khung giờ sớm nhất / muộn nhất (epoch giây với lịch theo ngày). */
  thoiDiemBatDau?: number | null;
  thoiDiemKetThuc?: number | null;
  diaBan?: { dichId: string; tenDich?: string | null }[];
  nguoiTaoId?: string | null;
  createdAt: string;
  updatedAt?: string;
}

/** Chi tiết lịch (`GET schedules/:id`). */
export interface ChiTietLichBT extends LichPhatBT {
  khungGio: (KhungGioBT & { id?: string })[];
  banTin: BanTinTrongLichBT[];
  phamVi: (PhamViLichBT & { id?: string })[];
}

/** Kết quả tạo / sửa / hủy / gửi lại lịch: lưu luôn thành công, phần gửi xuống báo riêng. */
export interface KetQuaThaoTacLichBT {
  id: string;
  lichPhatId?: string;
  trangThai?: string;
  guiXuong?: { thanhCong?: boolean; ketQua?: KetQuaGuiBT | null; loi?: unknown } | null;
}

export interface LichPhatBTVao {
  tenLichPhat: string;
  loaiLich: LoaiLichBT;
  phanVungManHinh: PhanVungManHinhBT;
  ngayBatDauPhat?: number;
  ngayKetThucPhat?: number;
  khungGio: KhungGioBT[];
  banTin: string[];
  phamVi: { dichId: string; bangDienTu?: string[] }[];
}

export interface BoLocLichBT {
  keyword?: string;
  trangThai?: string;
  nguonId?: string;
  tuThoiDiem?: number;
  denThoiDiem?: number;
  page: number;
  pageSize: number;
}

/* --------------------------------- Phát / điều khiển ------------------------------ */

export interface PhatBTVao {
  /** uuid bản tin (`ban_tin.id`, Kho nội dung). */
  banTinId: string;
  /** Phát khẩn cấp nhiều bản tin một lần (DV011 `DanhSachBanTin`); phần tử đầu = `banTinId`. */
  banTinIds?: string[];
  /** Mã địa bàn nhận. */
  phamVi: string[];
  bangDienTu?: string[];
  phanVungManHinh?: PhanVungManHinhBT;
}

/** MaLenh DV017: 0 âm lượng, 2 dừng/tạm dừng/phát tiếp, 3 khởi động lại, 4 nâng cấp (1 bật/tắt màn hình CHƯA làm). */
export type MaLenhBT = '0' | '2' | '3' | '4';

export interface DieuKhienBTVao {
  bangDienTu: string[];
  diaBan?: string[];
  maLenh: MaLenhBT;
  thamSo: string;
}

/* ------------------------------------ Trạng thái ----------------------------------- */

export interface TrangThaiThietBiBT {
  /** uuid thiết bị (device.id). */
  deviceId: string;
  /** BangDienTuID (mã thiết bị). */
  bangDienTuId: string;
  tenThietBi?: string | null;
  nguonId?: string | null;
  tenDiaBan?: string | null;
  maNhaCungCap?: string | null;
  tenNhaCungCap?: string | null;
  phuongThucKetNoi?: string | null;
  /** Máy chủ tính sẵn (có khoá Redis còn hạn); không trả thì CMS suy từ `thoiDiem`. */
  dangKetNoi?: boolean;
  /** '0' đang tắt, '1' đang mở; null khi chưa từng báo / mất kết nối. */
  trangThai?: string | null;
  trangThaiKetNoi?: string | null;
  noiDungPhat?: string[] | string | null;
  /** Epoch giây của gói DV016 mới nhất. */
  thoiDiem?: number | null;
  nhietDo?: number | null;
  dungLuongSuDungKb?: number | null;
  phienBanUngDung?: string | null;
  /** Âm lượng 0–100 bảng báo trong `ThongTinThietBi.AmLuong` (ngoài CV276); null khi không gửi. */
  amLuong?: number | null;
  nguon?: string | null;
  /** Lịch còn hiệu lực sửa gần nhất có phạm vi chứa bảng; `daCapNhat` khi bảng đã báo DV014. */
  lichPhatMoiNhat?: { lichPhatId: string; tenLichPhat: string; thoiDiem: number; daCapNhat: boolean } | null;
}

/** Lọc trạng thái của danh sách bảng điện tử. */
export type TrangThaiLocBT = 'dang_ket_noi' | 'dang_phat' | 'mat_ket_noi';

export interface BoLocTrangThaiBT {
  nguonId?: string;
  keyword?: string;
  maNhaCungCap?: string;
  trangThaiHienThi?: TrangThaiLocBT;
  page: number;
  pageSize: number;
}

/** Ba ô tóm tắt danh sách bảng điện tử (`devices/summary`). */
export interface TomTatThietBiBT {
  tong: number;
  dangKetNoi: number;
  dangPhat: number;
  khongPhat: number;
  matKetNoi: number;
}

/* -------------------------------------- Nhật ký ------------------------------------- */

export interface BoLocNhatKyBT {
  nguonId?: string;
  keyword?: string;
  tuThoiDiem?: number;
  denThoiDiem?: number;
  page?: number;
  pageSize?: number;
}

/** DV013 — bản tin đã phát. */
export interface DongNhatKyPhatBT {
  id: string;
  bangDienTuId: string;
  tenThietBi?: string | null;
  nguonId?: string | null;
  tenDiaBan?: string | null;
  banTinMa?: string | null;
  banTinId?: string | null;
  tieuDe?: string | null;
  loaiBanTin?: string | null;
  mucDoUuTien?: string | null;
  vungPhat?: string | null;
  loaiLinhVuc?: string | null;
  thoiLuong?: string | null;
  noiDung?: string[];
  thoiDiemBatDau: number;
  nguonKichHoat?: string | null;
  maNhaCungCap?: string | null;
  /** Bản tin còn tệp trong kho để xem lại. */
  coTep?: boolean;
}

/** DV014 / DV015 — lịch phát thiết bị nhận / hủy. */
export interface DongNhatKyLichBT {
  id: string;
  lichPhatMa: string;
  /** uuid lịch nếu khớp `bt_lich_phat`. */
  lichPhatId?: string | null;
  tenLichPhat?: string | null;
  bangDienTuId?: string | null;
  tenThietBi?: string | null;
  nguonId?: string | null;
  tenDiaBan?: string | null;
  soBanTin?: number | null;
  goiTin?: unknown;
  thoiDiem: number;
}

/* ------------------------------------- Lời gọi -------------------------------------- */

type ThamSo = Record<string, string | number | undefined | null>;
const tv = (tham: object): string => chuoiTruyVan(tham as ThamSo);

const ma = (id: string): string => encodeURIComponent(id);

export const bangTinApi = {
  /* Lịch phát */
  lichDanhSach: (tham: BoLocLichBT, signal?: AbortSignal): Promise<TrangDuLieu<LichPhatBT>> =>
    goi(`${GOC}/schedules${tv(tham)}`, { signal }),
  lichChiTiet: (id: string, signal?: AbortSignal): Promise<ChiTietLichBT> =>
    goi(`${GOC}/schedules/${ma(id)}`, { signal }),
  lichTao: (than: LichPhatBTVao): Promise<KetQuaThaoTacLichBT> =>
    goi(`${GOC}/schedules`, { phuongThuc: 'POST', than }),
  lichSua: (id: string, than: LichPhatBTVao): Promise<KetQuaThaoTacLichBT> =>
    goi(`${GOC}/schedules/${ma(id)}`, { phuongThuc: 'PUT', than }),
  lichHuy: (id: string, lyDo?: string): Promise<KetQuaThaoTacLichBT> =>
    goi(`${GOC}/schedules/${ma(id)}/cancel`, { phuongThuc: 'POST', than: { lyDo } }),
  lichGuiLai: (id: string): Promise<KetQuaThaoTacLichBT> =>
    goi(`${GOC}/schedules/${ma(id)}/resend`, { phuongThuc: 'POST', than: {} }),

  /* Phát / điều khiển */
  phatNgay: (than: PhatBTVao): Promise<KetQuaGuiBT | null> =>
    goi(`${GOC}/broadcast/now`, { phuongThuc: 'POST', than }),
  phatKhanCap: (than: PhatBTVao & { lyDo: string }): Promise<KetQuaGuiBT | null> =>
    goi(`${GOC}/broadcast/emergency`, { phuongThuc: 'POST', than }),
  dieuKhien: (than: DieuKhienBTVao): Promise<KetQuaGuiBT | null> =>
    goi(`${GOC}/devices/commands`, { phuongThuc: 'POST', than }),

  /* Trạng thái */
  trangThaiThietBi: (
    tham: BoLocTrangThaiBT,
    signal?: AbortSignal,
  ): Promise<TrangDuLieu<TrangThaiThietBiBT>> => goi(`${GOC}/devices/status${tv(tham)}`, { signal }),
  tomTatThietBi: (tham: { nguonId?: string }, signal?: AbortSignal): Promise<TomTatThietBiBT> =>
    goi(`${GOC}/devices/summary${tv(tham)}`, { signal }),
  xuatDanhSachThietBi: (
    tham: Omit<BoLocTrangThaiBT, 'page' | 'pageSize'>,
  ): Promise<{ soDong: number; tong: number }> =>
    taiTepTuApi(`${GOC}/devices/status/export${tv(tham)}`, 'danh-sach-bang-dien-tu.xlsx'),

  /* Nhật ký DV013/DV014/DV015 + Excel */
  nhatKyPhat: (tham: BoLocNhatKyBT, signal?: AbortSignal): Promise<TrangDuLieu<DongNhatKyPhatBT>> =>
    goi(`${GOC}/play-logs${tv(tham)}`, { signal }),
  nhatKyLich: (tham: BoLocNhatKyBT, signal?: AbortSignal): Promise<TrangDuLieu<DongNhatKyLichBT>> =>
    goi(`${GOC}/schedule-logs${tv(tham)}`, { signal }),
  nhatKyHuy: (tham: BoLocNhatKyBT, signal?: AbortSignal): Promise<TrangDuLieu<DongNhatKyLichBT>> =>
    goi(`${GOC}/cancel-logs${tv(tham)}`, { signal }),
  xuatNhatKyPhat: (tham: BoLocNhatKyBT): Promise<{ soDong: number; tong: number }> =>
    taiTepTuApi(`${GOC}/play-logs/export${tv(tham)}`, 'nhat-ky-ban-tin-da-phat.xlsx'),
  xuatNhatKyLich: (tham: BoLocNhatKyBT): Promise<{ soDong: number; tong: number }> =>
    taiTepTuApi(`${GOC}/schedule-logs/export${tv(tham)}`, 'nhat-ky-lich-phat-bang-tin.xlsx'),
  xuatNhatKyHuy: (tham: BoLocNhatKyBT): Promise<{ soDong: number; tong: number }> =>
    taiTepTuApi(`${GOC}/cancel-logs/export${tv(tham)}`, 'nhat-ky-huy-lich-bang-tin.xlsx'),
  xemLaiTep: (id: string, signal?: AbortSignal): Promise<LinkTepBT> =>
    goi(`${GOC}/play-logs/${ma(id)}/playback`, { signal }),
};

export type BangTinApi = typeof bangTinApi;

/* ------------------------------------ Tiện ích ------------------------------------- */

/** Danh sách uuid bản tin của lịch, theo thứ tự phát. */
export function idBanTinCuaLich(l: Pick<ChiTietLichBT, 'banTin'>): string[] {
  return [...(l.banTin ?? [])].sort((a, b) => (a.thuTu ?? 0) - (b.thuTu ?? 0)).map((b) => b.id);
}

/**
 * Kết quả gửi xuống của một thao tác lịch → khuôn chung `KetQuaGuiBT`. Gửi thất bại mà không có
 * danh sách lỗi từng bảng thì gộp `guiXuong.loi` (chuỗi hoặc đối tượng) thành một lỗi.
 */
export function ketQuaGuiCuaLich(kq: KetQuaThaoTacLichBT | null | undefined): KetQuaGuiBT | null {
  const g = kq?.guiXuong;
  if (!g) return null;
  const ketQua: KetQuaGuiBT = { ...(g.ketQua ?? {}) };
  if (g.thanhCong === false && (ketQua.loi ?? []).length === 0) {
    const thongBao = typeof g.loi === 'string' ? g.loi : g.loi ? JSON.stringify(g.loi) : 'GUI_XUONG_LOI';
    ketQua.loi = [{ maLoi: 'GUI_XUONG_LOI', thongBao }];
  }
  return ketQua;
}

/** Thiết bị đang kết nối: theo cờ máy chủ, không có thì có gói trạng thái trong 180 giây. */
export function dangKetNoiBT(d: TrangThaiThietBiBT, bayGio = Date.now() / 1000): boolean {
  if (d.dangKetNoi !== undefined) return d.dangKetNoi;
  return typeof d.thoiDiem === 'number' && bayGio - d.thoiDiem <= 180;
}
